import React, { useEffect, useRef, useCallback } from 'react'
import { connect } from 'react-redux'
import jwtDecode from 'jwt-decode';
import { CryptoHelper } from 'dsilo-ui-components'
import { compareDatesByMinutes } from '../../utility/utils'

// Debug logger - enable via localStorage.setItem('debugSSO', 'true')
const debugLog = (...args) => {
	const isDebug = localStorage.getItem('debugSSO') === 'true'
	if (isDebug) console.log('[SilentSSO]', ...args)
}

// Helper function outside component to avoid recreation on each render
const buildSsoUrl = (configData) => {
	if (configData?.[`ssoEnable`] && configData?.[`ssoConfig`]) {
		let decryptedConfig = configData[`ssoConfig`]
		if (decryptedConfig && decryptedConfig.enable) {
			const finalConfig = {
				...decryptedConfig,
				orgId: configData?.orgId,
			}
			localStorage.setItem(`ssoConfig`, window.btoa(JSON.stringify(finalConfig)))
			return finalConfig?.saml?.ssoURL?.includes("SAMLRequest")
				? finalConfig?.saml?.ssoURL
				: finalConfig?.saml?.ssoURL + `?SAMLRequest=${finalConfig?.SAMLRequest}`
		}
	}
	return null
}

const SilentSSO = (props) => {

	const { emailCheckData, isCheckEmailLLSELoading } = props.login
	const { restart } = props
	// Use ref instead of state to avoid stale closure issues and prevent re-renders
	const lastTriggeredATRef = useRef(null)
	const intervalRef = useRef(null)
	// Ref to always call the latest version of startSilentSSO from interval
	const startSilentSSORef = useRef(null)

	// Memoized startSilentSSO that always has fresh values
	const startSilentSSO = useCallback(async () => {
		const userEmailReport = localStorage.getItem('userEmailReport')
		const token = localStorage.getItem('jwt_access_token');
		const decoded = token ? jwtDecode(token) : {};
		const THIRTY_MINUTES = 30; // 30 minutes

		// Convert decoded.session from ms to minutes
		const sessionFromToken = decoded?.session && typeof decoded?.session === 'number'
			? decoded.session / (60 * 1000)
			: THIRTY_MINUTES

		// Take the smaller of the two
		const effectiveSession = Math.min(sessionFromToken, THIRTY_MINUTES);

		const shouldTrigger = !compareDatesByMinutes(new Date(), lastTriggeredATRef.current, effectiveSession) || props.e401

		debugLog("=== SilentSSO Debug Start ===")
		debugLog("1. isCheckEmailLLSELoading:", isCheckEmailLLSELoading)
		debugLog("2. emailCheckData?.isCheckEmailLLSEComplete:", emailCheckData?.isCheckEmailLLSEComplete)
		debugLog("3. userEmailReport exists:", !!userEmailReport)
		debugLog("4. shouldTrigger:", shouldTrigger)
		debugLog("5. props.e401:", props.e401)
		
		// If email check is actively loading, wait for it to complete (even for 401)
		// If not loading: for 401 errors bypass the complete check, otherwise require it
		const isLoading = isCheckEmailLLSELoading === true
		const emailCheckReady = !isLoading && (props.e401 || emailCheckData?.isCheckEmailLLSEComplete)
		debugLog("6. isLoading:", isLoading)
		debugLog("6.1. emailCheckReady:", emailCheckReady)
		
		if (emailCheckReady && userEmailReport && shouldTrigger) {
			debugLog("✓ Passed preconditions check")
			let parsedEmailCheckData
			try {
				parsedEmailCheckData = JSON.parse(window.atob(userEmailReport))
				debugLog("6. parsedEmailCheckData:", parsedEmailCheckData)
			} catch (e) {
				debugLog("✗ EXIT: Invalid userEmailReport - failed to parse")
				return // Invalid userEmailReport, exit early
			}
			
			const isSsoLogin = localStorage.getItem('isSsoLogin')
			debugLog("7. isSsoLogin:", isSsoLogin)
			debugLog("8. parsedEmailCheckData type:", typeof parsedEmailCheckData)
			
			if (typeof parsedEmailCheckData === 'object') {
				debugLog("9. adEnable:", parsedEmailCheckData?.adEnable)
				debugLog("10. ssoEnable:", parsedEmailCheckData?.ssoEnable)
				debugLog("11. ssoConfig exists:", !!parsedEmailCheckData?.ssoConfig)
				
				if (parsedEmailCheckData?.adEnable && parsedEmailCheckData?.adConfig) {
					debugLog("→ AD login path (not SSO)")
					// AD login handling
				} else if (parsedEmailCheckData?.ssoEnable && parsedEmailCheckData?.ssoConfig && isSsoLogin) {
				// } else if (parsedEmailCheckData?.ssoEnable && parsedEmailCheckData?.ssoConfig) {
					debugLog("✓ SSO path - conditions met")
					if (parsedEmailCheckData && Object.keys(parsedEmailCheckData)?.length) {
						const url = buildSsoUrl(parsedEmailCheckData)
						debugLog("12. buildSsoUrl result:", url)
						if (!url) {
							debugLog("✗ EXIT: buildSsoUrl returned null")
							return
						}
						
						const isInIframe = window.self !== window.top
						debugLog("13. isInIframe:", isInIframe)
						debugLog("14. Will trigger (!isInIframe || props.e401):", !isInIframe || props.e401)
						
						// Only trigger if not in iframe, OR if in iframe and got 401
						if (!isInIframe || props.e401) {
							const iframe = document.getElementById('silent-sso-iframe')
							debugLog("15. iframe element found:", !!iframe)
							if (iframe) {
								debugLog("✓ SUCCESS: Updating iframe src to trigger silent SSO", url)
								iframe.src = url // Update the iframe's src to trigger silent SSO
								lastTriggeredATRef.current = new Date()
							} else {
								debugLog("✗ EXIT: iframe element not found")
							}
						} else {
							debugLog("✗ EXIT: In iframe and no 401 - skipping refresh")
						}
					} else {
						debugLog("✗ EXIT: parsedEmailCheckData is empty")
					}
				} else {
					debugLog("✗ EXIT: SSO conditions not met - ssoEnable:", parsedEmailCheckData?.ssoEnable, "ssoConfig:", !!parsedEmailCheckData?.ssoConfig, "isSsoLogin:", isSsoLogin)
				}
			} else {
				debugLog("✗ EXIT: parsedEmailCheckData is not an object")
			}
		} else {
			debugLog("✗ EXIT: Failed preconditions - isLoading:", isLoading, "emailCheckReady:", emailCheckReady, "userEmailReport:", !!userEmailReport, "shouldTrigger:", shouldTrigger)
		}
		debugLog("=== SilentSSO Debug End ===")
	}, [props.e401, emailCheckData, isCheckEmailLLSELoading])

	// Keep the ref updated with the latest function
	useEffect(() => {
		startSilentSSORef.current = startSilentSSO
	}, [startSilentSSO])

	// Trigger SSO check when restart prop changes (tab visibility or 401)
	useEffect(() => {
		console.log("[SilentSSO] useEffect triggered - restart:", restart, "e401:", props.e401)
		if (restart) {
			debugLog("Silent SSO Component restarted due to restart prop change", restart)
			startSilentSSO()
		}
	}, [restart, startSilentSSO])

	// IFRAME Silent SSO - Set up interval only once on mount
	// The interval calls the ref to always get the latest function
	useEffect(() => {
		const token = localStorage.getItem('jwt_access_token');
		const decoded = token ? jwtDecode(token) : {};
		debugLog("SilentSSO mounted, decoded token:", decoded)
		const defaultSessionTimeout = 30 * 60 * 1000; // 30 minutes in ms
		const refreshTime = Math.min(decoded?.session ?? defaultSessionTimeout, defaultSessionTimeout);
		debugLog("SilentSSO interval set to:", refreshTime, "ms")
		
		// Set up interval that calls the ref (always has fresh values)
		intervalRef.current = setInterval(() => {
			if (startSilentSSORef.current) {
				startSilentSSORef.current()
			}
		}, refreshTime) // Every 30 minutes or 70% of session time

		/**
		 * To recognize the iframe refresh
		 * handleMessage - event on refresh
		 */
		const handleMessage = (event) => {
			if (event.data === "iframeRefreshed") {
				debugLog("Iframe refreshed (message from iframe)!");
				// Iframe refreshed successfully
			}
		};

		window.addEventListener("message", handleMessage);

		return () => {
			if (intervalRef.current !== null) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
			// To remove window message
			window.removeEventListener("message", handleMessage);
		};
	}, []) // Empty dependency - interval set up only once

	// Keep for potential future use with encrypted configs
	// eslint-disable-next-line no-unused-vars
	const getDecryptedConfig = async (encryptedConfig, iv) => {
		if (encryptedConfig && typeof encryptedConfig === 'string' && iv) {
			const decryptedConfig = await CryptoHelper.decodeData(encryptedConfig, iv, props?.secretKey)
			if (decryptedConfig) return JSON.parse(decryptedConfig)
			return null
		}
		return encryptedConfig
	}

	return (
		<iframe
			id="silent-sso-iframe"
			// src="/silent-sso"
			style={{ display: 'none' }} // Keep the iframe hidden
			title="Silent SSO"
		/>
	)
}

const mapDispatchToProps = {}

const mapStateToProps = state => {
	return {
		login: state.login
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(SilentSSO)
// export default SilentSSO